// Hussar Treasury celebration audio — original synthesized sound identity.
//
// Identity: heavy gold coins deliberately dropped one by one into a wooden
// treasure chest. Premium, weighty, metallic, controlled. Never casino,
// never "cha-ching", never cartoon. All audio is synthesized in-browser with
// WebAudio (original assets only — no sampled/copyrighted material).

let _ctx = null;

function audioCtx() {
  if (!_ctx) _ctx = new (window.AudioContext || window.webkitAudioContext)();
  if (_ctx.state === "suspended") _ctx.resume();
  return _ctx;
}

function noiseBuffer(ctx, seconds) {
  const buf = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * seconds), ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  return buf;
}

// "Inside the chest" spatial depth: short damped feedback delay.
function chestSpace(ctx, master) {
  const input = ctx.createGain();
  const dry = ctx.createGain(); dry.gain.value = 0.85;
  const delay = ctx.createDelay(0.3); delay.delayTime.value = 0.085;
  const damp = ctx.createBiquadFilter(); damp.type = "lowpass"; damp.frequency.value = 1700;
  const fb = ctx.createGain(); fb.gain.value = 0.26;
  const wet = ctx.createGain(); wet.gain.value = 0.2;
  input.connect(dry); dry.connect(master);
  input.connect(delay); delay.connect(damp); damp.connect(fb); fb.connect(delay);
  damp.connect(wet); wet.connect(master);
  return input;
}

// One heavy coin landing in the chest. weight scales pitch/impact (heavier = lower, fuller).
function coinDrop(ctx, dest, t, { weight = 1, pan = 0 } = {}) {
  const chain = ctx.createGain();
  const panner = ctx.createStereoPanner();
  panner.pan.value = pan;
  chain.connect(panner); panner.connect(dest);

  // Strike transient: short bright tick of the impact.
  const n = ctx.createBufferSource(); n.buffer = noiseBuffer(ctx, 0.05);
  const nf = ctx.createBiquadFilter(); nf.type = "bandpass"; nf.frequency.value = 4200; nf.Q.value = 1.1;
  const ng = ctx.createGain();
  ng.gain.setValueAtTime(0.45 * weight, t);
  ng.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
  n.connect(nf); nf.connect(ng); ng.connect(chain);
  n.start(t); n.stop(t + 0.06);

  // Metallic inharmonic partials — the coin's gold resonance. Heavier coins sit lower.
  const scale = Math.pow(1 / weight, 0.35);
  const partials = [1975, 2980, 4260, 6180].map((f) => f * scale);
  const gains = [0.5, 0.3, 0.18, 0.09];
  const decays = [0.17, 0.13, 0.1, 0.07].map((d) => d * (0.8 + weight * 0.45));
  partials.forEach((f, i) => {
    const o = ctx.createOscillator(); o.type = "sine";
    o.frequency.setValueAtTime(f * 1.035, t);
    o.frequency.exponentialRampToValueAtTime(f, t + 0.03);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gains[i] * weight * 0.55, t + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, t + decays[i]);
    o.connect(g); g.connect(chain);
    o.start(t); o.stop(t + decays[i] + 0.05);
  });

  // Wooden chest body resonance under the impact.
  const w = ctx.createOscillator(); w.type = "sine";
  w.frequency.setValueAtTime(150, t);
  w.frequency.exponentialRampToValueAtTime(68, t + 0.09);
  const wf = ctx.createBiquadFilter(); wf.type = "lowpass"; wf.frequency.value = 320;
  const wg = ctx.createGain();
  wg.gain.setValueAtTime(0.0001, t);
  wg.gain.exponentialRampToValueAtTime(0.32 * weight, t + 0.008);
  wg.gain.exponentialRampToValueAtTime(0.0001, t + 0.22 * (0.7 + weight * 0.4));
  w.connect(wf); wf.connect(wg); wg.connect(chain);
  w.start(t); w.stop(t + 0.32);
}

// Subdued chest "close" after the final coin — the value is secured.
function chestClose(ctx, dest, t, weight = 1) {
  const n = ctx.createBufferSource(); n.buffer = noiseBuffer(ctx, 0.25);
  const f = ctx.createBiquadFilter(); f.type = "lowpass"; f.frequency.value = 240;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.22 * weight, t + 0.015);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.28);
  n.connect(f); f.connect(g); g.connect(dest);
  n.start(t); n.stop(t + 0.3);
}

// Deliberate, weighty rhythms — never frantic.
const PATTERNS = {
  SUBTLE: [
    { t: 0, weight: 0.85, pan: -0.1 },
    { t: 0.36, weight: 0.68, pan: 0.12 },
  ],
  HUSSAR: [
    { t: 0, weight: 1.0, pan: -0.18 },
    { t: 0.46, weight: 0.9, pan: 0.2 },
    { t: 0.96, weight: 1.45, pan: 0 },
  ],
  JACKPOT: [
    { t: 0, weight: 0.8, pan: -0.25 },
    { t: 0.3, weight: 0.88, pan: 0.22 },
    { t: 0.6, weight: 0.98, pan: -0.12 },
    { t: 0.9, weight: 1.05, pan: 0.15 },
    { t: 1.0, weight: 0.5, pan: -0.3 },
    { t: 1.28, weight: 1.5, pan: 0 },
  ],
};

const INTENSITY_GAIN = { MICRO: 0.55, STANDARD: 0.8, MAJOR: 1.0, MILESTONE: 1.2 };

export function playCelebration(mode = "HUSSAR", intensity = "STANDARD") {
  try {
    if (mode === "OFF") return;
    const ctx = audioCtx();
    const master = ctx.createGain();
    master.gain.value = 0.9 * (INTENSITY_GAIN[intensity] ?? 0.8);
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -14; comp.ratio.value = 6;
    master.connect(comp); comp.connect(ctx.destination);
    const space = chestSpace(ctx, master);

    const now = ctx.currentTime + 0.02;
    const drops = PATTERNS[mode] || PATTERNS.HUSSAR;
    drops.forEach((d) => coinDrop(ctx, space, now + d.t, d));
    const last = drops[drops.length - 1];
    chestClose(ctx, space, now + last.t + 0.14, mode === "SUBTLE" ? 0.5 : 1);
  } catch { /* audio unavailable — celebration continues silently */ }
}

// Haptics mirror the coin impacts: one pulse per drop, strongest on the final coin.
const HAPTICS = {
  SUBTLE: [22, 330, 18],
  HUSSAR: [38, 420, 34, 460, 110],
  JACKPOT: [28, 270, 28, 270, 30, 270, 32, 340, 140],
};

export function hapticCelebration(mode = "HUSSAR") {
  try {
    if (navigator.vibrate) navigator.vibrate(HAPTICS[mode] || HAPTICS.HUSSAR);
  } catch { /* haptics unsupported */ }
}

// TREASURY INITIALIZED — one deep, heavy coin secured into the chest.
// Founding capital, not profit: single deliberate drop, restrained close.
export function playInitialization() {
  try {
    const ctx = audioCtx();
    const master = ctx.createGain();
    master.gain.value = 1.0;
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -14; comp.ratio.value = 6;
    master.connect(comp); comp.connect(ctx.destination);
    const space = chestSpace(ctx, master);
    const now = ctx.currentTime + 0.02;
    coinDrop(ctx, space, now, { weight: 1.9, pan: 0 });
    chestClose(ctx, space, now + 0.34, 1.2);
  } catch { /* audio unavailable */ }
}

export function hapticInitialization() {
  try {
    if (navigator.vibrate) navigator.vibrate([150]);
  } catch { /* haptics unsupported */ }
}
